import type { WidgetPalette, WidgetPaletteColors } from './types';

const VAR_MAP: Record<keyof WidgetPaletteColors, string> = {
  primary: '--orca-primary',
  primaryDark: '--orca-primary-dark',
  primaryAccent: '--orca-primary-accent',
  primaryMuted: '--orca-primary-muted',
  primarySurface: '--orca-primary-surface',
  channelHover: '--orca-channel-hover',
  focusRing: '--orca-focus-ring',
  surface: '--orca-surface',
  surfaceMuted: '--orca-surface-muted',
  surfaceElevated: '--orca-surface-elevated',
  border: '--orca-border',
  text: '--orca-text',
  textMuted: '--orca-text-muted',
  inputBg: '--orca-input-bg',
  inputBorder: '--orca-input-border',
  inputText: '--orca-input-text',
  cancelBg: '--orca-cancel-bg',
  cancelText: '--orca-cancel-text',
};

export interface PaletteOption {
  id: string;
  label: string;
  /** Representative brand color for pickers / swatches. */
  swatch: string;
}

function brand(
  light: WidgetPaletteColors,
  dark: WidgetPaletteColors,
): WidgetPalette {
  return { light, dark };
}

/**
 * Named presets so the widget can be branded differently per app without every embedder having
 * to hand-roll a full color set (e.g. DCP domains vs. ECP business mail).
 */
export const PRESET_PALETTES: Record<string, WidgetPalette> = {
  default: brand(
    {
      primary: '#0039a7',
      primaryDark: '#002d85',
      primaryAccent: '#0052cc',
      primaryMuted: '#b3c9e8',
      primarySurface: '#e8f0fa',
      channelHover: '#d6e4f5',
      focusRing: 'rgba(0, 57, 167, 0.25)',
      surface: '#ffffff',
      surfaceMuted: '#f8fafc',
      surfaceElevated: '#ffffff',
      border: '#e2e8f0',
      text: '#0f172a',
      textMuted: '#334155',
      inputBg: '#ffffff',
      inputBorder: '#cbd5e1',
      inputText: '#0f172a',
      cancelBg: '#e2e8f0',
      cancelText: '#334155',
    },
    {
      primary: '#3b82f6',
      primaryDark: '#2563eb',
      primaryAccent: '#60a5fa',
      primaryMuted: '#1e4a8a',
      primarySurface: 'rgba(59, 130, 246, 0.24)',
      channelHover: 'rgba(59, 130, 246, 0.38)',
      focusRing: 'rgba(96, 165, 250, 0.35)',
      surface: '#111827',
      surfaceMuted: '#0b1220',
      surfaceElevated: '#1e293b',
      border: '#334155',
      text: '#f1f5f9',
      textMuted: '#94a3b8',
      inputBg: '#1e293b',
      inputBorder: '#475569',
      inputText: '#f8fafc',
      cancelBg: '#334155',
      cancelText: '#e2e8f0',
    },
  ),
  dcp: brand(
    {
      primary: '#0039a7',
      primaryDark: '#002d85',
      primaryAccent: '#0052cc',
      primaryMuted: '#9db8e0',
      primarySurface: '#e6eefb',
      channelHover: '#c9daf3',
      focusRing: 'rgba(0, 57, 167, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#f3f7fc',
      surfaceElevated: '#ffffff',
      border: '#d7e3f4',
      text: '#0b1f44',
      textMuted: '#3d567a',
      inputBg: '#ffffff',
      inputBorder: '#b7c9e3',
      inputText: '#0b1f44',
      cancelBg: '#e2e8f0',
      cancelText: '#334155',
    },
    {
      primary: '#4f8cff',
      primaryDark: '#2f6fed',
      primaryAccent: '#7aadff',
      primaryMuted: '#1f3f7a',
      primarySurface: 'rgba(79, 140, 255, 0.22)',
      channelHover: 'rgba(79, 140, 255, 0.36)',
      focusRing: 'rgba(122, 173, 255, 0.4)',
      surface: '#0c1424',
      surfaceMuted: '#080f1b',
      surfaceElevated: '#162338',
      border: '#2a3f5f',
      text: '#eaf1ff',
      textMuted: '#9bb0d0',
      inputBg: '#162338',
      inputBorder: '#35507a',
      inputText: '#eaf1ff',
      cancelBg: '#2a3f5f',
      cancelText: '#d7e3f4',
    },
  ),
  ecp: brand(
    {
      primary: '#6d28d9',
      primaryDark: '#5b21b6',
      primaryAccent: '#7c3aed',
      primaryMuted: '#d8b4fe',
      primarySurface: '#f3e8ff',
      channelHover: '#e9d5ff',
      focusRing: 'rgba(109, 40, 217, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#faf5ff',
      surfaceElevated: '#ffffff',
      border: '#e9d5ff',
      text: '#2e1065',
      textMuted: '#6b21a8',
      inputBg: '#ffffff',
      inputBorder: '#d8b4fe',
      inputText: '#2e1065',
      cancelBg: '#ede9fe',
      cancelText: '#5b21b6',
    },
    {
      primary: '#a78bfa',
      primaryDark: '#8b5cf6',
      primaryAccent: '#c4b5fd',
      primaryMuted: '#4c1d95',
      primarySurface: 'rgba(167, 139, 250, 0.22)',
      channelHover: 'rgba(167, 139, 250, 0.36)',
      focusRing: 'rgba(196, 181, 253, 0.4)',
      surface: '#140b24',
      surfaceMuted: '#0d0718',
      surfaceElevated: '#1f1436',
      border: '#3b2760',
      text: '#f5f3ff',
      textMuted: '#c4b5fd',
      inputBg: '#1f1436',
      inputBorder: '#4c1d95',
      inputText: '#f5f3ff',
      cancelBg: '#3b2760',
      cancelText: '#e9d5ff',
    },
  ),
  ocean: brand(
    {
      primary: '#0e7490',
      primaryDark: '#155e75',
      primaryAccent: '#0891b2',
      primaryMuted: '#a5f3fc',
      primarySurface: '#ecfeff',
      channelHover: '#cffafe',
      focusRing: 'rgba(14, 116, 144, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#f0fdfa',
      surfaceElevated: '#ffffff',
      border: '#a5f3fc',
      text: '#083344',
      textMuted: '#155e75',
      inputBg: '#ffffff',
      inputBorder: '#67e8f9',
      inputText: '#083344',
      cancelBg: '#e0f2fe',
      cancelText: '#0c4a6e',
    },
    {
      primary: '#22d3ee',
      primaryDark: '#06b6d4',
      primaryAccent: '#67e8f9',
      primaryMuted: '#164e63',
      primarySurface: 'rgba(34, 211, 238, 0.2)',
      channelHover: 'rgba(34, 211, 238, 0.34)',
      focusRing: 'rgba(103, 232, 249, 0.4)',
      surface: '#07161c',
      surfaceMuted: '#041016',
      surfaceElevated: '#0f2430',
      border: '#164e63',
      text: '#ecfeff',
      textMuted: '#a5f3fc',
      inputBg: '#0f2430',
      inputBorder: '#155e75',
      inputText: '#ecfeff',
      cancelBg: '#164e63',
      cancelText: '#cffafe',
    },
  ),
  forest: brand(
    {
      primary: '#15803d',
      primaryDark: '#166534',
      primaryAccent: '#16a34a',
      primaryMuted: '#bbf7d0',
      primarySurface: '#f0fdf4',
      channelHover: '#dcfce7',
      focusRing: 'rgba(21, 128, 61, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#f7fef9',
      surfaceElevated: '#ffffff',
      border: '#bbf7d0',
      text: '#14532d',
      textMuted: '#166534',
      inputBg: '#ffffff',
      inputBorder: '#86efac',
      inputText: '#14532d',
      cancelBg: '#dcfce7',
      cancelText: '#166534',
    },
    {
      primary: '#4ade80',
      primaryDark: '#22c55e',
      primaryAccent: '#86efac',
      primaryMuted: '#14532d',
      primarySurface: 'rgba(74, 222, 128, 0.2)',
      channelHover: 'rgba(74, 222, 128, 0.34)',
      focusRing: 'rgba(134, 239, 172, 0.4)',
      surface: '#07140c',
      surfaceMuted: '#040e08',
      surfaceElevated: '#102318',
      border: '#14532d',
      text: '#f0fdf4',
      textMuted: '#bbf7d0',
      inputBg: '#102318',
      inputBorder: '#166534',
      inputText: '#f0fdf4',
      cancelBg: '#14532d',
      cancelText: '#dcfce7',
    },
  ),
  sunset: brand(
    {
      primary: '#c2410c',
      primaryDark: '#9a3412',
      primaryAccent: '#ea580c',
      primaryMuted: '#fed7aa',
      primarySurface: '#fff7ed',
      channelHover: '#ffedd5',
      focusRing: 'rgba(194, 65, 12, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#fffaf5',
      surfaceElevated: '#ffffff',
      border: '#fed7aa',
      text: '#7c2d12',
      textMuted: '#9a3412',
      inputBg: '#ffffff',
      inputBorder: '#fdba74',
      inputText: '#7c2d12',
      cancelBg: '#ffedd5',
      cancelText: '#9a3412',
    },
    {
      primary: '#fb923c',
      primaryDark: '#f97316',
      primaryAccent: '#fdba74',
      primaryMuted: '#7c2d12',
      primarySurface: 'rgba(251, 146, 60, 0.22)',
      channelHover: 'rgba(251, 146, 60, 0.36)',
      focusRing: 'rgba(253, 186, 116, 0.4)',
      surface: '#1a0f08',
      surfaceMuted: '#120a05',
      surfaceElevated: '#2a1810',
      border: '#7c2d12',
      text: '#fff7ed',
      textMuted: '#fed7aa',
      inputBg: '#2a1810',
      inputBorder: '#9a3412',
      inputText: '#fff7ed',
      cancelBg: '#7c2d12',
      cancelText: '#ffedd5',
    },
  ),
  slate: brand(
    {
      primary: '#475569',
      primaryDark: '#334155',
      primaryAccent: '#64748b',
      primaryMuted: '#cbd5e1',
      primarySurface: '#f1f5f9',
      channelHover: '#e2e8f0',
      focusRing: 'rgba(71, 85, 105, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#f8fafc',
      surfaceElevated: '#ffffff',
      border: '#e2e8f0',
      text: '#0f172a',
      textMuted: '#475569',
      inputBg: '#ffffff',
      inputBorder: '#cbd5e1',
      inputText: '#0f172a',
      cancelBg: '#e2e8f0',
      cancelText: '#334155',
    },
    {
      primary: '#94a3b8',
      primaryDark: '#64748b',
      primaryAccent: '#cbd5e1',
      primaryMuted: '#334155',
      primarySurface: 'rgba(148, 163, 184, 0.2)',
      channelHover: 'rgba(148, 163, 184, 0.34)',
      focusRing: 'rgba(203, 213, 225, 0.35)',
      surface: '#0f172a',
      surfaceMuted: '#020617',
      surfaceElevated: '#1e293b',
      border: '#334155',
      text: '#f8fafc',
      textMuted: '#94a3b8',
      inputBg: '#1e293b',
      inputBorder: '#475569',
      inputText: '#f8fafc',
      cancelBg: '#334155',
      cancelText: '#e2e8f0',
    },
  ),
  rose: brand(
    {
      primary: '#be123c',
      primaryDark: '#9f1239',
      primaryAccent: '#e11d48',
      primaryMuted: '#fecdd3',
      primarySurface: '#fff1f2',
      channelHover: '#ffe4e6',
      focusRing: 'rgba(190, 18, 60, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#fff8f8',
      surfaceElevated: '#ffffff',
      border: '#fecdd3',
      text: '#881337',
      textMuted: '#9f1239',
      inputBg: '#ffffff',
      inputBorder: '#fda4af',
      inputText: '#881337',
      cancelBg: '#ffe4e6',
      cancelText: '#9f1239',
    },
    {
      primary: '#fb7185',
      primaryDark: '#f43f5e',
      primaryAccent: '#fda4af',
      primaryMuted: '#881337',
      primarySurface: 'rgba(251, 113, 133, 0.22)',
      channelHover: 'rgba(251, 113, 133, 0.36)',
      focusRing: 'rgba(253, 164, 175, 0.4)',
      surface: '#1a0a10',
      surfaceMuted: '#12060b',
      surfaceElevated: '#2a121a',
      border: '#881337',
      text: '#fff1f2',
      textMuted: '#fecdd3',
      inputBg: '#2a121a',
      inputBorder: '#9f1239',
      inputText: '#fff1f2',
      cancelBg: '#881337',
      cancelText: '#ffe4e6',
    },
  ),
  amber: brand(
    {
      primary: '#b45309',
      primaryDark: '#92400e',
      primaryAccent: '#d97706',
      primaryMuted: '#fde68a',
      primarySurface: '#fffbeb',
      channelHover: '#fef3c7',
      focusRing: 'rgba(180, 83, 9, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#fffdf5',
      surfaceElevated: '#ffffff',
      border: '#fde68a',
      text: '#78350f',
      textMuted: '#92400e',
      inputBg: '#ffffff',
      inputBorder: '#fcd34d',
      inputText: '#78350f',
      cancelBg: '#fef3c7',
      cancelText: '#92400e',
    },
    {
      primary: '#fbbf24',
      primaryDark: '#f59e0b',
      primaryAccent: '#fcd34d',
      primaryMuted: '#78350f',
      primarySurface: 'rgba(251, 191, 36, 0.2)',
      channelHover: 'rgba(251, 191, 36, 0.34)',
      focusRing: 'rgba(252, 211, 77, 0.4)',
      surface: '#171208',
      surfaceMuted: '#100c05',
      surfaceElevated: '#261c0c',
      border: '#78350f',
      text: '#fffbeb',
      textMuted: '#fde68a',
      inputBg: '#261c0c',
      inputBorder: '#92400e',
      inputText: '#fffbeb',
      cancelBg: '#78350f',
      cancelText: '#fef3c7',
    },
  ),
  midnight: brand(
    {
      primary: '#312e81',
      primaryDark: '#1e1b4b',
      primaryAccent: '#4338ca',
      primaryMuted: '#c7d2fe',
      primarySurface: '#eef2ff',
      channelHover: '#e0e7ff',
      focusRing: 'rgba(49, 46, 129, 0.28)',
      surface: '#ffffff',
      surfaceMuted: '#f8f9ff',
      surfaceElevated: '#ffffff',
      border: '#c7d2fe',
      text: '#1e1b4b',
      textMuted: '#3730a3',
      inputBg: '#ffffff',
      inputBorder: '#a5b4fc',
      inputText: '#1e1b4b',
      cancelBg: '#e0e7ff',
      cancelText: '#312e81',
    },
    {
      primary: '#818cf8',
      primaryDark: '#6366f1',
      primaryAccent: '#a5b4fc',
      primaryMuted: '#312e81',
      primarySurface: 'rgba(129, 140, 248, 0.22)',
      channelHover: 'rgba(129, 140, 248, 0.36)',
      focusRing: 'rgba(165, 180, 252, 0.4)',
      surface: '#0b0a1a',
      surfaceMuted: '#060512',
      surfaceElevated: '#16142b',
      border: '#312e81',
      text: '#eef2ff',
      textMuted: '#c7d2fe',
      inputBg: '#16142b',
      inputBorder: '#4338ca',
      inputText: '#eef2ff',
      cancelBg: '#312e81',
      cancelText: '#e0e7ff',
    },
  ),
};

export const PRESET_PALETTE_OPTIONS: PaletteOption[] = [
  { id: 'default', label: 'Default', swatch: '#0039a7' },
  { id: 'dcp', label: 'DCP', swatch: '#0039a7' },
  { id: 'ecp', label: 'ECP', swatch: '#6d28d9' },
  { id: 'ocean', label: 'Ocean', swatch: '#0e7490' },
  { id: 'forest', label: 'Forest', swatch: '#15803d' },
  { id: 'sunset', label: 'Sunset', swatch: '#c2410c' },
  { id: 'slate', label: 'Slate', swatch: '#475569' },
  { id: 'rose', label: 'Rose', swatch: '#be123c' },
  { id: 'amber', label: 'Amber', swatch: '#b45309' },
  { id: 'midnight', label: 'Midnight', swatch: '#312e81' },
];

/** Merges a named preset (if any) with explicit per-instance overrides; overrides win. */
export function resolvePalette(palette?: WidgetPalette, paletteName?: string): WidgetPalette {
  const preset = paletteName ? PRESET_PALETTES[paletteName] : undefined;
  if (!preset && !palette) {
    return {};
  }
  return {
    light: { ...preset?.light, ...palette?.light },
    dark: { ...preset?.dark, ...palette?.dark },
  };
}

/** Applies (or clears) a set of palette colors as inline custom properties on the shadow host. */
export function applyPaletteColors(host: HTMLElement, colors?: WidgetPaletteColors): void {
  for (const key of Object.keys(VAR_MAP) as (keyof WidgetPaletteColors)[]) {
    const cssVar = VAR_MAP[key];
    const value = colors?.[key];
    if (value) {
      host.style.setProperty(cssVar, value);
    } else {
      host.style.removeProperty(cssVar);
    }
  }
}
